import React from 'react';
import {
  Flex,
  Box,
  Heading,
  Text,
  DataTable,
  LoadingOrbit,
  Card,
  CardHeader,
  CardContent,
  Button,
  Select,
  Divider,
  TagBadge,
  badgeVariantToTagColor,
} from '@chatsdet/ui';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import {
  useOrganizationsQuery,
  useSuperadminWorkflowsQuery,
  useCopyWorkflowMutation,
  type SuperadminWorkflow,
  type WorkflowListParams,
} from './useSuperadminData';

const flowTypeOptions = [
  { id: '', label: 'All Types' },
  { id: 'setup', label: 'Setup' },
  { id: 'test', label: 'Test' },
  { id: 'monitoring', label: 'Monitoring' },
];

const flowTypeVariants: Record<string, 'secondary' | 'default' | 'outline'> = {
  setup: 'secondary',
  test: 'default',
  monitoring: 'outline',
};

export function SuperadminWorkflows() {
  // Filters
  const [filterOrgId, setFilterOrgId] = React.useState<string>('');
  const [filterFlowType, setFilterFlowType] = React.useState<string>('');
  const [filterSearch, setFilterSearch] = React.useState<string>('');
  const [page, setPage] = React.useState(1);

  // Copy form state
  const [selectedWorkflow, setSelectedWorkflow] = React.useState<SuperadminWorkflow | null>(null);
  const [targetOrgId, setTargetOrgId] = React.useState<string>('');
  const [newWorkflowName, setNewWorkflowName] = React.useState<string>('');
  const [copySuccess, setCopySuccess] = React.useState<string | null>(null);

  const organizationsQuery = useOrganizationsQuery();

  const queryParams: WorkflowListParams = React.useMemo(
    () => ({
      orgId: filterOrgId || undefined,
      flowType: (filterFlowType as 'setup' | 'test' | 'monitoring') || undefined,
      search: filterSearch || undefined,
      page,
      pageSize: 25,
    }),
    [filterOrgId, filterFlowType, filterSearch, page]
  );

  const workflowsQuery = useSuperadminWorkflowsQuery(queryParams);
  const copyWorkflowMutation = useCopyWorkflowMutation();

  const orgOptions = React.useMemo(() => {
    const orgs = organizationsQuery.data?.organizations ?? [];
    return [
      { id: '', label: 'All Organizations' },
      ...orgs.map((org) => ({ id: org.id, label: org.name })),
    ];
  }, [organizationsQuery.data]);

  const targetOrgOptions = React.useMemo(() => {
    const orgs = organizationsQuery.data?.organizations ?? [];
    return orgs
      .filter((org) => !selectedWorkflow || org.id !== selectedWorkflow.orgId)
      .map((org) => ({ id: org.id, label: org.name }));
  }, [organizationsQuery.data, selectedWorkflow]);

  const handleCopyWorkflow = () => {
    if (!selectedWorkflow || !targetOrgId) return;

    setCopySuccess(null);
    copyWorkflowMutation.mutate(
      {
        sourceWorkflowId: selectedWorkflow.id,
        targetOrgId,
        newWorkflowName: newWorkflowName.trim() || undefined,
      },
      {
        onSuccess: (result) => {
          if (result.data) {
            const { copied } = result.data;
            setCopySuccess(
              `Copied workflow "${result.data.workflow.name}" with ${copied.nodes} nodes, ${copied.edges} edges, ${copied.credentials} credentials`
            );
            setTargetOrgId('');
            setNewWorkflowName('');
          }
        },
      }
    );
  };

  const handleSelectWorkflow = (workflow: SuperadminWorkflow) => {
    setSelectedWorkflow(workflow);
    setCopySuccess(null);
    setTargetOrgId('');
    setNewWorkflowName('');
  };

  const workflowColumns: ColumnDef<StockFeatures, SuperadminWorkflow>[] = React.useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <Flex direction="column" gap="xs">
            <Text weight="medium">{row.original.name}</Text>
            {row.original.description && (
              <Text size="xs" className="text-muted-foreground line-clamp-1">
                {row.original.description}
              </Text>
            )}
          </Flex>
        ),
      },
      {
        accessorKey: 'orgName',
        header: 'Organization',
        cell: ({ row }) => <Text size="sm">{row.original.orgName}</Text>,
      },
      {
        accessorKey: 'flowType',
        header: 'Type',
        cell: ({ row }) => (
          <TagBadge
            name={row.original.flowType}
            color={badgeVariantToTagColor(flowTypeVariants[row.original.flowType] ?? 'secondary')}
            hideDot
          />
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <TagBadge
            name={row.original.status}
            color={badgeVariantToTagColor(
              row.original.status === 'approved' ? 'success' : 'secondary'
            )}
            hideDot
          />
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: ({ row }) => (
          <Text size="sm" className="text-muted-foreground">
            {new Date(row.original.createdAt).toLocaleDateString()}
          </Text>
        ),
      },
    ],
    []
  );

  return (
    <Box className="space-y-6">
      <Flex direction="column" gap="xl">
        {/* Filters */}
        <Card padding="lg">
          <CardHeader>
            <Heading level={3}>Workflows</Heading>
            <Text size="sm" className="text-muted-foreground mt-1">
              Browse and copy workflows across organizations.
            </Text>
          </CardHeader>
          <CardContent>
            <Flex gap="md" wrap align="end">
              <Box className="min-w-[200px]">
                <Select
                  label="Organization"
                  placeholder="Filter by organization"
                  items={orgOptions}
                  selectedKey={filterOrgId}
                  onSelectionChange={(key) => {
                    setFilterOrgId(String(key));
                    setPage(1);
                  }}
                  isDisabled={organizationsQuery.isLoading}
                />
              </Box>
              <Box className="min-w-[150px]">
                <Select
                  label="Flow Type"
                  placeholder="Filter by type"
                  items={flowTypeOptions}
                  selectedKey={filterFlowType}
                  onSelectionChange={(key) => {
                    setFilterFlowType(String(key));
                    setPage(1);
                  }}
                />
              </Box>
              <Box className="min-w-[200px]">
                <Text size="xs" className="text-muted-foreground mb-1 block">
                  Search
                </Text>
                <input
                  value={filterSearch}
                  onChange={(e) => {
                    setFilterSearch(e.target.value);
                    setPage(1);
                  }}
                  className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background"
                  placeholder="Search by name..."
                />
              </Box>
            </Flex>
          </CardContent>
        </Card>

        {/* Workflows List */}
        <Card padding="lg">
          <CardHeader>
            <Flex align="center" justify="between" wrap gap="md">
              <div>
                <Heading level={4}>
                  {workflowsQuery.data?.pagination.totalItems ?? 0} Workflows
                </Heading>
              </div>
              {workflowsQuery.isLoading && <LoadingOrbit size="sm" />}
            </Flex>
          </CardHeader>
          <CardContent>
            {workflowsQuery.isLoading ? (
              <Flex align="center" justify="center" className="py-10">
                <LoadingOrbit size="lg" />
              </Flex>
            ) : workflowsQuery.isError ? (
              <Text className="text-destructive">
                {workflowsQuery.error instanceof Error
                  ? workflowsQuery.error.message
                  : 'Failed to load workflows'}
              </Text>
            ) : workflowsQuery.data?.data?.length ? (
              <Flex direction="column" gap="lg">
                <DataTable
                  columns={workflowColumns}
                  data={workflowsQuery.data.data}
                  onRowClick={handleSelectWorkflow}
                  serverPagination={{
                    page: workflowsQuery.data.pagination.page,
                    pageSize: workflowsQuery.data.pagination.pageSize,
                    pageCount: workflowsQuery.data.pagination.totalPages,
                    totalItems: workflowsQuery.data.pagination.totalItems,
                    onPageChange: setPage,
                  }}
                />

                {selectedWorkflow && (
                  <>
                    <Divider />
                    <Box>
                      <Heading level={4} className="text-base mb-3">
                        Copy "{selectedWorkflow.name}"
                      </Heading>
                      <Text size="sm" className="text-muted-foreground mb-4">
                        From: {selectedWorkflow.orgName} ({selectedWorkflow.flowType})
                      </Text>

                      <Flex gap="md" wrap align="end" className="mb-4">
                        <Box className="min-w-[240px]">
                          <Select
                            label="Target Organization"
                            placeholder="Select target organization"
                            items={targetOrgOptions}
                            selectedKey={targetOrgId}
                            onSelectionChange={(key) => setTargetOrgId(String(key))}
                            isDisabled={organizationsQuery.isLoading}
                          />
                        </Box>
                        <Box className="min-w-[200px]">
                          <Text size="xs" className="text-muted-foreground mb-1 block">
                            New Name (optional)
                          </Text>
                          <input
                            value={newWorkflowName}
                            onChange={(e) => setNewWorkflowName(e.target.value)}
                            className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background"
                            placeholder={`${selectedWorkflow.name} (Copy)`}
                          />
                        </Box>
                        <Button
                          onPress={handleCopyWorkflow}
                          isDisabled={copyWorkflowMutation.isPending || !targetOrgId}
                        >
                          {copyWorkflowMutation.isPending ? 'Copying...' : 'Copy Workflow'}
                        </Button>
                      </Flex>

                      {copyWorkflowMutation.isError && (
                        <Text className="text-destructive text-sm">
                          {copyWorkflowMutation.error instanceof Error
                            ? copyWorkflowMutation.error.message
                            : 'Failed to copy workflow'}
                        </Text>
                      )}

                      {copySuccess && <Text className="text-success text-sm">{copySuccess}</Text>}
                    </Box>
                  </>
                )}
              </Flex>
            ) : (
              <Text className="text-muted-foreground">
                No workflows found. Try adjusting your filters.
              </Text>
            )}
          </CardContent>
        </Card>
      </Flex>
    </Box>
  );
}
